import { h, Component } from 'preact';
import Store from 'store/store';
import 'store/cookies';
import pure from 'utils/pure';
import { pages } from 'pages/powsterWebsite_pages';
import { accessNested, loadScript, sortObjectByOrder, triggerAnim, joinClasses } from 'utils/utils';
import hoverDetection from 'utils/hoverDetection';
import { smoothScrollTo } from 'utils/smoothScroll';
import * as cookies from 'utils/cookies';
import tabbingDetection from 'utils/tabbingDetection';
import Header from 'components/shared/header/header';
import DynamicPageLoader from 'components/shared/dynamicPageLoader/dynamicPageLoader';
import Transition from 'components/core/transition/transition';
import LandscapeOverlay from 'components/bolt/landscapeOverlay/landscapeOverlay';
import Footer from 'components/shared/footer/footer';
import CookieBanner from 'components/shared/cookies/banner';
import CookiePreferenceCenter from 'components/shared/cookies/preferences';
import Subscribe from 'components/shared/subscribe/subscribe';
import s from 'base/powsterWebsite/powsterWebsiteApp.sss';

const isServer = typeof window === 'undefined';
const TRANSITION_DURATION = 1000;

import { marked } from 'marked';
// Custom markdown renderer
const markedRenderer = new marked.Renderer();
// Allow links with a leading + to open in a new tab e.g. [text](+http://test.com)
markedRenderer.link = ({ href, title, text }) => {
	let attributes = {};
	if (title) attributes.title = title;
	if (href && href[0] === '+') {
		href = href.slice(1);
		attributes.target = '_blank';
	}
	try {
		href = encodeURI(href).replace(/%25/g, '%');
	} catch (e) {
		return text;
	}
	attributes.href = href;
	let attrString = Object.keys(attributes).map(key => key + '="' + attributes[key] + '"').join(' ');
	return '<a ' + attrString + '>' + text + '</a>';
};
marked.use({
	renderer: markedRenderer
});

export default @pure class PowsterWebsiteApp extends Component {
	constructor(props) {
		super(props);
		this.handleScroll = this.handleScroll.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.getIsDesktopSize = this.getIsDesktopSize.bind(this);
		this.getSize = this.getSize.bind(this);
		this.getDims = this.getDims.bind(this);
		this.getDevice = this.getDevice.bind(this);
		this.handleOrientationChange = this.handleOrientationChange.bind(this);
		this.toggleSubscribeForm = this.toggleSubscribeForm.bind(this);
		this.hideSubscribePrompt = this.hideSubscribePrompt.bind(this);

		let appState = props.initialStore;
		if (!isServer) {
			if (!appState.cookies) {
				appState.cookies = {};
			}
			appState.cookies.allowed = cookies.load();
		}

		this.state = {
			...this.state,
			appState: appState,
			footerActive: false
		};

		this.state.orientation = accessNested(props, 'initialStore.client.orientation', 'portrait');

		Store.set(props.initialStore);

		Store.on('update', () => {
			const store = Store.get();
			this.setState({ appState: store });
		});

		if (!isServer) {
			const powsterGACode = accessNested(appState, 'appData.apis.tracking.powsterGATracking.powsterCustomGATitle', 'G-3FTEM7J315');
			cookies.register('performance', () => loadScript(`https://www.googletagmanager.com/gtag/js?id=${powsterGACode}`));
			cookies.register('functionality', () => true);

			// NOTE: Should be in a store file instead
			Store.on('SET_ACTIVE_PAGE', page => {
				const store = Store.get();
				if (typeof page !== 'string') {
					page = page.id;
				}
				let pageData = store.appData.pages[page];
				if (pageData && page !== store.activePage) {
					if (!Store.get().featuredProjectsScroll) {
						this.scrollToTop();
					}
					store.set({ activePage: page });
					triggerAnim(this.base, 'page-transitioned');
					let currentPath = store.appData.pages[store.activePage].path;
					let relativeSiteRoot = currentPath.split('/').slice(2).fill('../').join('');
					let url = relativeSiteRoot + '.' + pageData.path;
					history.pushState({ activePage: page }, '', url);
				}
			});

			window.addEventListener('popstate', e => {
				let page = e.state && e.state.activePage;
				const store = Store.get();
				const pages = store.appData.pages;
				if (!page) {
					let path = location.pathname.replace(/\/[^/]+\.html$/, '/');
					let matchingPages = Object.keys(pages).filter(id => pages[id] && path.endsWith(pages[id].path));
					page = matchingPages.sort((a, b) => pages[a].path.length - pages[b].path.length).pop();
				}
				if (page && pages[page] && page !== store.activePage) {
					// document.body.scrollTop = '0';
					store.set({ activePage: page });
					triggerAnim(this.base, 'page-transitioned');
					this.scrollToTop();
				}
			});

			Store.emit('LANDING', { page: props.initialStore.activePage });
			document.body.classList.add('js-loaded');
		}
	}

	componentDidMount() {
		document.body.classList.add('hide-preloader');
		this.handleResize();
		hoverDetection();
		tabbingDetection();
		Store.on('HEADER_TOGGLE', this.handlePreventScroll);
		window.addEventListener('scroll', this.handleScroll);
		// window.addEventListener('wheel', this.handleScroll);
		window.addEventListener('touchmove', this.handleScroll);
		window.addEventListener('resize', this.handleResize);
		window.addEventListener('orientationchange', this.handleOrientationChange);
		this.handleOrientationChange();
		// window.addEventListener('popstate', this.scrollToTop);
		if ('scrollRestoration' in history) {
			// prevent browser from scrolling back to trop
			history.scrollRestoration = 'manual';
		}
		if (window.location.search && window.location.search.match(/newsletter=visible/)) {
			if (!this.state.subscribeFormOpen) this.toggleSubscribeForm();
		}
		this.maybeShowSubscribePrompt();
	}

	componentWillUnmount() {
		super.componentWillUnmount();
		window.removeEventListener('scroll', this.handleScroll);
		window.removeEventListener('touchmove', this.handleScroll);
		window.removeEventListener('orientationchange', this.handleOrientationChange);
		// window.removeEventListener('popstate', this.scrollToTop);
	}

	componentDidUpdate(prevProps, prevState) {
		let store = this.state.appState;
		let activePage = store.activePage;
		if (activePage && activePage !== prevState.appState.activePage) {
			const copy = store.appData.copy || {};
			const title = copy[`$${activePage.toUpperCase()}_META_TITLE`] || copy.$META_TITLE || 'Powster';
			document.title = title;
		}
		if (store.cookies.allowed !== prevState.appState.cookies.allowed) {
			this.maybeShowSubscribePrompt();
		}
	}

	scrollToTop() {
		setTimeout(() => smoothScrollTo('root', 0, 0, { force: true }), TRANSITION_DURATION / 2);
	}

	maybeShowSubscribePrompt() {
		const cookieBannerDismissed = !!accessNested(this.state, 'appState.cookies.allowed');
		if (!cookieBannerDismissed) {
			return;
		}
		const hasHidePromptCookie = document.cookie.match(/hidePowSubscribePrompt=true/);
		if (hasHidePromptCookie) {
			return;
		}
		setTimeout(() => {
			this.setState({
				subscribePromptVisible: true
			});
		}, 1000);
	}

	handleOrientationChange() {
		let orientation = 'portrait';
		switch (window.orientation) {
			case 0:
			case 180:
				// Portrait
				orientation = 'portrait';
				break;
			case -90:
			case 90:
				orientation = 'landscape';
				break;
		}
		this.setState({ orientation });
	}

	getDims() {
		return {
			width: window.innerWidth,
			height: window.innerHeight
		};
	}

	getDevice() {
		if (typeof window !== 'undefined') {
			const agent = ('' + navigator.userAgent).toLowerCase();
			if (agent.indexOf('android') !== -1) {
				return 'android';
			}
			if (agent.indexOf('iphone') !== -1) {
				return 'iphone';
			}
			if (agent.indexOf('ipad') !== -1) {
				return 'ipad';
			}
		}
		return 'desktop';
	}

	getSize(dimensions = this.getDims()) {
		const breakpoints = [[767, 'mobile'], [1024, 'tablet'], [1366, 'tabletLandscape'], [1600, 'desktop'], [Infinity, 'largeDesktop']];
		for (let i = 0; i < breakpoints.length; i++) {
			if (dimensions.width < breakpoints[i][0]) {
				return breakpoints[i][1];
			}
		}
		return 'unknown';
	}

	getIsDesktopSize(size = this.getSize()) {
		return (size === 'desktop' || size === 'largeDesktop' || size === 'superLargeDesktop');
	}


	handleResize() {
		let vh = window.innerHeight;
		document.documentElement.style.setProperty('--vh', (vh * 0.01) + 'px');
		document.documentElement.style.setProperty('--windowHeight', vh + 'px');
		let isDesktopSize = this.getIsDesktopSize();
		let dims = this.getDims();
		let deviceSize = this.getSize(dims);
		let client = this.state.appState.client;
		if (!client) {
			return;
		}
		if (deviceSize !== client.deviceSize || !client.deviceSize) {
			Store.get().client.set({ deviceSize });
		}
		if (isDesktopSize !== client.isDesktopSize) {
			Store.get().client.set({ isDesktopSize });
		}
		if (dims.width !== client.dimensions.width || dims.height !== client.dimensions.height) {
			Store.get().client.set({ dimensions: dims });
		}
	}

	handleScroll(e) {
		e.preventDefault();
		let activeActivationPoint = this.$footer.base.offsetTop - this.$footer.base.clientHeight / 2;
		if (this.state.appState.client.isDesktopSize || this.state.appState.client.deviceSize.match(/tablet/)) {
			activeActivationPoint = this.$footer.base.offsetTop - this.$footer.base.clientHeight - window.innerHeight / 3;
		}
		if (window.pageYOffset >= activeActivationPoint) {
			this.setState({ footerActive: true });
		} else {
			this.setState({ footerActive: false });
		}
	}

	handlePreventScroll = (menuOpen) => document.body.style.overflow = menuOpen ? 'hidden' : 'auto';

	toggleSubscribeForm() {
		if (!this.state.subscribeFormOpen) {
			this.hideSubscribePrompt();
		}
		this.setState({
			subscribeFormOpen: !this.state.subscribeFormOpen
		});
	}

	hideSubscribePrompt(subscribed) {
		this.setState({
			subscribePromptVisible: false
		});
		const userSubscribed = subscribed === 'subscribed';
		const hasHidePromptCookie = document.cookie.match(/hidePowSubscribePrompt=true/);
		if (!userSubscribed && hasHidePromptCookie) {
			// we don't want a one-day cookie to override a 6-month cookie
			return;
		}
		const canSetCookie = accessNested(this.state, 'appState.cookies.allowed.functionality') === true;
		const cookieLifeSpanInDays = userSubscribed ? 183 : 1;
		if (canSetCookie) {
			document.cookie = `hidePowSubscribePrompt=true; max-age=${cookieLifeSpanInDays * 24 * 60 * 60}`;
		}
	}

	render(props, state) {
		let { appState, footerActive } = state;
		const { activePage, client, cookies } = appState;

		let page = pages[activePage];

		if (props.renderedPage) {
			page = props.renderedPage;
		}

		const orientation = state.orientation;
		const isMobileLandscape = (client && (client.deviceSize === 'mobile')) && (orientation === 'landscape');

		let cookieBanner;
		if (!isServer && !cookies || !cookies.allowed || !cookies.allowed.necessary) {
			cookieBanner = <CookieBanner />;
		}

		const sortedSocials = sortObjectByOrder(appState.appData.meta.social);

		return (
			<div class={joinClasses(s.container, activePage)} id="app">
				<Header
					appData={appState.appData}
					client={client}
					activePage={activePage}
					socials={sortedSocials}
					key="header"
				/>
				<Transition
					class={s.transition}
					duration={TRANSITION_DURATION}
					enteringClass={s.entering}
					leavingClass={s.leaving}
					visibleClass={s.visible}
				>
					<DynamicPageLoader id={page.id} page={page} data={appState.appData.pages[page.id]} props={{ client }} key="page" />
				</Transition>
				<Footer
					data={appState.appData.footer}
					store={appState}
					ref={ref => this.$footer = ref}
					active={footerActive}
					client={client}
					socials={sortedSocials}
					key="footer"
					toggleSubscribeForm={this.toggleSubscribeForm}
					subscribeFormOpen={this.state.subscribeFormOpen}
					powSubscribeActive={appState.appData.options?.powSubscribeActive}
				/>
				<CookiePreferenceCenter
					active={cookies.showPreferenceCenter}
					selected={cookies.allowed}
					key="cookiePreferenceCenter"
				/>
				{cookieBanner}
				{appState.appData.options?.powSubscribeActive && (
					<Subscribe
						isPrompt
						formOpen={this.state.subscribeFormOpen}
						toggleForm={this.toggleSubscribeForm}
						hideSubscribePrompt={this.hideSubscribePrompt}
						promptVisible={this.state.subscribePromptVisible}
					/>
				)}
				{isMobileLandscape && <LandscapeOverlay key="landscape-overlay" />}
			</div>
		);
	}
}
